import { SQ_FT_TO_M2 } from '@util/constants';
import { ConvertAreaFn } from '@util/customHooks/useMeasurement';
import { formatNumberToStringWithCommas } from '@util/listings';

export function parseAreaString(areaStr: string): {
  values: number[];
  isRange: boolean;
  isPlus: boolean;
} {
  const str = areaStr.toString().replace(',', '').trim();
  // Range format: "5000 - 6000"
  if (str.includes('-')) {
    return {
      values: str.split('-').map((s) => parseFloat(s.trim())),
      isRange: true,
      isPlus: false,
    };
  }

  // Plus format: "5000+"
  if (str.endsWith('+')) {
    return {
      values: [parseFloat(str.replace('+', '').trim())],
      isRange: false,
      isPlus: true,
    };
  }
  // Single value format: "5000"
  return {
    values: [parseFloat(str)],
    isRange: false,
    isPlus: false,
  };
}

export function convertAreaToSqM(value: number, isSqM: boolean): number {
  return isSqM ? value * SQ_FT_TO_M2 : value;
}

export function formatAreaValues(
  values: number[],
  isRange: boolean,
  isPlus: boolean,
  formatter: (val: number) => string,
): string {
  const formatted = values.map(formatter);

  // Format range: "$5,000 - $6,000"
  if (isRange && values.length === 2) {
    return `${formatted[0]} - ${formatted[1]}`;
  }

  // Format plus: "$5,000 +"
  if (isPlus) {
    return `${formatted[0]} +`;
  }
  // Format single value: "$5,000"
  return formatted[0] ?? '';
}

export function formatPricePerLivingUnit(
  listPrice: number,
  area: string | number,
  isSqM: boolean,
): string {
  const { values, isRange, isPlus } = parseAreaString(area.toString());

  if (values.some((v) => isNaN(v) || v <= 0)) return '-';

  return formatAreaValues(values, isRange, isPlus, (areaVal) => {
    const converted = convertAreaToSqM(areaVal, isSqM);
    const price = listPrice / converted;
    return isSqM
      ? `$${formatNumberToStringWithCommas(parseFloat(price.toFixed(2)))}`
      : price.toFixed(2);
  });
}

export const getCorrectAreaToDisplay = (
  convertArea: ConvertAreaFn,
  t: any,
  propertySubType?: string,
  lotSizeAcres?: number | null,
  squareFeet?: number | string,
): string | undefined => {
  if (propertySubType === 'Land' && lotSizeAcres) {
    return `${lotSizeAcres} ${t('Acres')}`;
  }

  if (!squareFeet) return undefined;

  const areaStr = squareFeet.toString().trim();

  const formatClean = (val: string) => {
    const { formattedArea } = convertArea(Number(val), 'sq.ft');
    const [num, unit] = formattedArea.split(' ');
    return { num, unit };
  };

  try {
    if (areaStr.includes('-')) {
      // Handle range: "5000 - 6000"
      const [start, end] = areaStr.split('-').map((v) => v.trim());
      const a = formatClean(start ?? '');
      const b = formatClean(end ?? '');
      return `${a.num} - ${b.num} ${b.unit}`;
    }

    if (areaStr.endsWith('+')) {
      // Handle plus: "5000+"
      const base = areaStr.replace('+', '').trim();
      const { num, unit } = formatClean(base ?? '');
      return `${num} + ${unit}`;
    }

    // Handle single value
    return convertArea(Number(areaStr), 'sq.ft').formattedArea;
  } catch {
    return undefined;
  }
};
